import {
  Icon,
  MenuGroupContent,
  MenuLineSeparator,
  MenuOption,
} from "@smartsuite/react-ui/lib";

import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import * as R from "remeda";
import { isTruthy } from "remeda";
import type {
  DynamicSection,
  DynamicSectionOption,
} from "../../../../../../../../types/line";
import { StyledIcon, StyledNoFields } from "./style";
import {
  MENU_MAX_HEIGHT,
  MENU_MAX_HEIGHT_MARGIN,
} from "../../../../../../../../utils/constants";
import { getTimezoneShortLabel } from "../../../../../../../../timezone/timezones";
import type { NumberPill } from "../../fields/NumberEditor/utils";
import { SolutionColorVariables } from "@smartsuite/types";
import { Tab } from "../TriggerDropdown/TriggerDropdown";
import { createPillAttrs } from "../ActionOptions";

export const TriggerOptions: React.FC<{
  tab?: Tab;
  title?: string;
  disableTitle?: boolean;
  onNavigateBack: () => void;
  addPill: (attrs: PillNodeAttrs | NumberPill) => void;
  options: DynamicSectionOption[];
  addDateOptions?: boolean;
  disabledOptionId?: string[];
  timezone?: string;
  hasSingleMenuItem?: boolean;
  searchPlaceholder?: string;
  filterSystemFields?: (item: {
    title: string;
    value: string;
    icon: string;
  }) => boolean;
  hidePast?: boolean;
  dateOptionsWithTime?: boolean;
  goSubMenu?: (options: DynamicSectionOption[], label: string) => void;
  loopingMenuHash?: string;
  loopingMenu?: DynamicSection | null;
}> = ({
  tab,
  title,
  onNavigateBack,
  addPill,
  options,
  addDateOptions,
  disabledOptionId,
  hasSingleMenuItem,
  searchPlaceholder,
  filterSystemFields,
  timezone = "",
  hidePast,
  dateOptionsWithTime,
  goSubMenu,
  disableTitle,
  loopingMenuHash,
  loopingMenu,
}) => {
  const [search, setSearch] = useState<string>("");
  const [searchIsTouched, setSearchIsTouched] = useState(false);
  let dateOptions: JSX.Element[] = [];
  const [menuHeight, setMenuHeight] = useState<number>(MENU_MAX_HEIGHT);
  const [unselectedOptions, setUnselectedOptions] =
    useState<DynamicSectionOption[]>(options);

  useEffect(() => {
    if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
      setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
    } else {
      setMenuHeight(MENU_MAX_HEIGHT);
    }
    window.addEventListener("resize", handleResize, false);

    return () => {
      window.removeEventListener("resize", handleResize, false);
    };
  }, []);

  useEffect(() => {
    const unselectedOptions = options.filter((option) =>
      R.isTruthy(disabledOptionId)
        ? !disabledOptionId.some((id) => id === getLabel(option))
        : true,
    );
    setUnselectedOptions(unselectedOptions);
  }, [disabledOptionId]);

  const handleResize = (): void => {
    if (window.innerHeight < menuHeight * 2 + MENU_MAX_HEIGHT_MARGIN) {
      setMenuHeight(window.innerHeight / 2 - MENU_MAX_HEIGHT_MARGIN);
    } else {
      setMenuHeight(MENU_MAX_HEIGHT);
    }
  };

  const dateFunctionOptions: Array<{
    title: string;
    value: string;
    icon: string;
  }> = [
    {
      title: t("phrase-builder.today"),
      value: "P0D",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.tomorrow"),
      value: "P1D",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.yesterday"),
      value: "-P1D",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.week-ago"),
      value: "-P7D",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.week-from-now"),
      value: "P7D",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.month-ago"),
      value: "-P1M",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.month-from-now"),
      value: "P1M",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.three-months-ago"),
      value: "-P3M",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.three-months-from-now"),
      value: "P3M",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.year-ago"),
      value: "-P1Y",
      icon: "date-select",
    },
    {
      title: t("phrase-builder.year-from-now"),
      value: "P1Y",
      icon: "date-select",
    },
  ];
  let unselectedDateOptions: typeof dateFunctionOptions = [];

  if (R.isTruthy(addDateOptions)) {
    unselectedDateOptions = dateFunctionOptions
      .filter(
        (option) =>
          (R.isTruthy(disabledOptionId)
            ? !disabledOptionId.some((id) => id === option.title)
            : true) &&
          // Case when dateFunctionOptions overlap trigger outputs
          options.find((mainOption) => mainOption.label === option.title) ===
            undefined,
      )
      .filter((option) =>
        search.length > 0
          ? option.title
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase())
          : true,
      )
      .filter((option) => {
        if (hidePast) {
          return !option.value.startsWith("-");
        }

        return true;
      });
    if (R.isTruthy(filterSystemFields)) {
      unselectedDateOptions = unselectedDateOptions.filter(filterSystemFields);
    }

    dateOptions = unselectedDateOptions.map((dateFunctionOption, index) => (
      <MenuOption
        key={`dateOption-${index}`}
        onClick={() =>
          addPill({
            icon: dateFunctionOption.icon,
            title: dateFunctionOption.title,
            value: JSON.stringify({
              value: dateFunctionOption.value,
              target: "date_function",
            }),
          })
        }
      >
        <>
          <StyledIcon>
            <Icon
              name={dateFunctionOption.icon}
              size="S"
              color={SolutionColorVariables.sc1}
            />
          </StyledIcon>
          {dateFunctionOption.title}
        </>
      </MenuOption>
    ));
  }

  const availableOptions = unselectedOptions
    .filter((option) =>
      search.length > 0
        ? option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        : true,
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (search.length >= 1) {
      setSearchIsTouched(true);
      return;
    }
    setSearchIsTouched(false);
  }, [search]);

  const groupedOptions: DynamicSectionOption[] = [];
  availableOptions.forEach((option) => {
    if (option.groupFieldId !== undefined) {
      if (isEntireFieldSelected(disabledOptionId, options, option)) {
        return;
      }

      if (option.labelSuffix === undefined) {
        const isSelectedNested = disabledOptionId?.some((id) => {
          return id.includes(option.label);
        });
        if (isSelectedNested) {
          return;
        }
      }
      const group = groupedOptions.find(
        (group) => group.groupFieldId === option.groupFieldId,
      );
      if (group === undefined) {
        groupedOptions.push({
          ...option,
          nestedOptions: [],
          groupFieldId: option.groupFieldId,
          label: option.label,
        });
      } else {
        const groupOption = groupedOptions.find(
          (group) => group.groupFieldId === option.groupFieldId,
        );
        if (groupOption) {
          groupOption.nestedOptions?.push(option);
        }
      }
    } else {
      groupedOptions.push({
        ...option,
        nestedOptions: [],
        groupFieldId: "",
        label: option?.label ?? "",
      });
    }
  });

  const optionRef = useRef<HTMLDivElement>(null);

  const onOptionClick = (option: DynamicSectionOption): void => {
    if (option.nestedOptions?.length) {
      if (goSubMenu) {
        goSubMenu([option, ...option.nestedOptions], option.label);
      }
    } else {
      let pillTitle = option.label;
      if (option.labelSuffix != null) {
        pillTitle = `${option.label}: ${option.labelSuffix ?? ""}`;
      }

      if (tab === Tab.LOOPING_FIELDS) {
        addPill(createPillAttrs(option, loopingMenuHash, loopingMenu?.type));
      } else {
        addPill({
          icon: option.icon,
          title: pillTitle,
          value: JSON.stringify({
            value: option.outputId,
            target: "trigger",
          }),
        });
      }
    }
  };

  return (
    <div ref={optionRef}>
      <MenuGroupContent
        title={
          disableTitle
            ? undefined
            : isTruthy(title)
              ? title
              : isTruthy(hasSingleMenuItem)
                ? t("field-popups.trigger-popup-single-header")
                : t("field-popups.trigger-popup")
        }
        subtitle={getTimezoneShortLabel(timezone)}
        searchPlaceholder={searchPlaceholder}
        onNavigateBack={hasSingleMenuItem ? () => {} : onNavigateBack}
        search={
          unselectedOptions.length + unselectedDateOptions.length > 5 ||
          searchIsTouched
        }
        onSearchChange={(text) => setSearch(text)}
        hideBackIcon={isTruthy(hasSingleMenuItem)}
        maxHeight={menuHeight}
      >
        {groupedOptions
          .filter((option) => !option.contextSpecific)
          .map((option, index) => {
            return (
              <MenuOption
                submenuArrow={
                  option.nestedOptions?.length ? "right" : undefined
                }
                key={option.outputId ?? index}
                onClick={() => onOptionClick(option)}
              >
                <>
                  <StyledIcon>
                    <Icon
                      name={option.icon}
                      size="S"
                      color={SolutionColorVariables.sc1}
                    />
                  </StyledIcon>
                  {getLabel(option)}
                </>
              </MenuOption>
            );
          })}

        {groupedOptions.filter((option) => !option.contextSpecific).length >
          1 &&
          groupedOptions.filter((option) => option.contextSpecific).length >
            0 && <MenuLineSeparator />}

        {groupedOptions
          .filter((option) => option.contextSpecific)
          .map((option, index) => {
            return (
              <MenuOption
                submenuArrow={
                  option.nestedOptions?.length ? "right" : undefined
                }
                key={option.outputId ?? index}
                onClick={() => onOptionClick(option)}
              >
                <>
                  <StyledIcon>
                    <Icon
                      name={option.icon}
                      size="S"
                      color={SolutionColorVariables.sc1}
                    />
                  </StyledIcon>
                  {getLabel(option)}
                </>
              </MenuOption>
            );
          })
          .concat(dateOptions)}

        {availableOptions.length === 0 &&
          unselectedDateOptions.length === 0 && (
            <StyledNoFields>No results</StyledNoFields>
          )}
      </MenuGroupContent>
    </div>
  );
};

const isEntireFieldSelected = (
  disabledOptionId: string[] | undefined,
  options: DynamicSectionOption[],
  option: DynamicSectionOption,
): boolean => {
  if (disabledOptionId === undefined) {
    return false;
  }
  const disabledOptionInSameGroup = disabledOptionId.filter((id) =>
    id.includes(option.label),
  );
  return disabledOptionInSameGroup.some((id) => {
    const option = options.find((option) => id === getLabel(option));
    return (
      option !== undefined &&
      option.labelSuffix === undefined &&
      option.groupFieldId !== undefined
    );
  });
};

const getLabel = (option: DynamicSectionOption): string => {
  return option.labelSuffix && !option.nestedOptions?.length
    ? `${option.label}: ${option.labelSuffix}`
    : option.label;
};
